/**
 * The Quality Guard vocabulary that crosses a package boundary.
 *
 * Quality Guard owns many more enums than these; the ones that live here are the ones more than
 * one package has to name. Each was spelled out at least twice before — the value array in
 * `qualityGuard/domain/ruleManifest.ts`, and again as an MCP contract constant, a display-label
 * map keyed by the same members, or a hand-typed union in the web app — with nothing forcing the
 * copies to agree. A member added to one and forgotten in another is a silent contract break: the
 * MCP `z.enum` rejects a readiness label the backend now emits, or a criticality renders as its
 * raw enum because the label map has no entry for it.
 *
 * So each set is declared exactly once, here, and everything else derives:
 * - Convex validators via `literalsToValidator(...)` in `qualityGuard/domain/ruleManifest.ts`,
 *   which re-exports these arrays so Convex call sites keep one import for the whole vocabulary;
 * - the MCP projection constants in `mcp-projection/contracts.ts`, which alias them;
 * - the TypeScript types below, off the arrays rather than off each other.
 *
 * `packages/convex/tests/qualityGuardEnumParity.test.ts` holds the line.
 */
/** The two axes every rule, finding and readiness projection is filed under. */
export const QUALITY_GUARD_AXIS_VALUES = ["completeness", "quality"];
/** How much a finding matters, most severe first. */
export const QUALITY_GUARD_CRITICALITY_VALUES = [
    "critical",
    "high",
    "recommended",
    "optional",
    "info",
];
/** The verdict a readiness projection carries for one scope. */
export const QUALITY_GUARD_READINESS_LABEL_VALUES = [
    "checking",
    "unready",
    "needs_attention",
    "unsafe",
    "accepted_risk",
    "ready",
];
/** Where a finding sends the reader to fix it. */
export const QUALITY_GUARD_REMEDIATION_ROUTE_VALUES = [
    "none",
    "claudia_change_candidate",
    "claudia_clarification",
    "accept_risk",
];
/** The coarse traffic-light a scope is painted with. */
export const QUALITY_GUARD_SEVERITY_COLOR_VALUES = ["none", "yellow", "red"];
/**
 * What each criticality is *called* on screen — deliberately a notch below the enum spelling, so
 * `high` reads as "Medium" rather than shouting at the reader.
 *
 * `satisfies` is what makes this a derivation rather than a second declaration: adding a
 * criticality without a label stops compiling, while the `as const` keeps the label literals
 * narrow for {@link QualityGuardCriticalityDisplayLabel}.
 */
export const QUALITY_GUARD_CRITICALITY_DISPLAY_LABELS = {
    critical: "High",
    high: "Medium",
    recommended: "Low",
    optional: "Optional",
    info: "Info",
};
/**
 * Whether one open issue is heavy enough to be counted.
 *
 * The exclusion every Quality Guard count makes, on either engine: an `info` readiness impact
 * and the two lightest criticalities are shown to a reader who opens the list but never added
 * into a badge, a chip or a roll-up. QG2's `low` severity is this vocabulary's `optional`, so
 * the same sentence covers both engines.
 *
 * Stated once because two readings of it disagree in public: the roll-up that writes a scope's
 * counts, and the header that counts the issues in hand while that row is still pending. A
 * scope holding only such issues has to show the same nothing before and after its row lands.
 */
export function isCountedQualityGuardIssue(issue) {
    return (issue.readinessImpact !== "info" &&
        issue.criticality !== "optional" &&
        issue.criticality !== "info");
}
/**
 * Whether one finding counts as covered — the state behind the `accepted_risk` word.
 *
 * Three things at once, and all three matter: something about it was accepted, nothing of it
 * is still open, and its readiness impact is not `info`. A finding with one key accepted and
 * another still open is not covered, however light the open key is: it is a finding the reader
 * still owes something on. A finding covered only by a dismissal was never accepted, so it is
 * not covered either — the caller says so by answering `hasAcceptedKey` false.
 *
 * Stated once because both readings of it are public: the roll-up that writes a scope's word,
 * and the header that reads the issues in hand while that row is still pending.
 */
export function isCoveredQualityGuardFinding(finding) {
    return finding.hasAcceptedKey && !finding.hasOpenKey && finding.readinessImpact !== "info";
}
/**
 * The word a scope is read by, from the issues standing against it.
 *
 * The ladder, in order, and the order is the whole point:
 *
 * 1. `unsafe` from any current issue whose readiness impact says so — over every issue on
 *    every axis, whatever its weight. Content named unsafe is a platform policy, not a
 *    counting question, so the count exclusions below never reach it. An issue can be too
 *    light to add to a badge and still stop the scope being relied on.
 * 2. `unready` on any counted critical, or any counted quality issue at the next weight down.
 * 3. `needs_attention` while anything counted is still open.
 * 4. `accepted_risk` when nothing counted is open and something is covered.
 * 5. `ready` otherwise.
 *
 * `checking` is row 0 and is never returned here: it is a fact about check rows rather than
 * about issues, and the caller that knows it folds it in.
 *
 * Callers classify their own rows — which are current, which are heavy enough to count, which
 * are covered — because only they can know. What lives here is the ladder itself, because two
 * readings of it are public at once: the roll-up row a scope stores, and the header that reads
 * the issues in hand while that row is still pending. They have to say the same word.
 */
export function deriveQualityGuardReadinessLabel(args) {
    if (args.current.some((row) => row.readinessImpact === "unsafe")) {
        return "unsafe";
    }
    if (args.counted.some((row) => row.criticality === "critical") ||
        args.counted.some((row) => row.axis === "quality" && row.criticality === "high")) {
        return "unready";
    }
    if (args.counted.length > 0) {
        return "needs_attention";
    }
    return args.hasAcceptedRisk ? "accepted_risk" : "ready";
}
export function formatQualityGuardCriticalityLabel(criticality) {
    if (typeof criticality !== "string") {
        return String(criticality);
    }
    return (QUALITY_GUARD_CRITICALITY_DISPLAY_LABELS[criticality] ?? criticality);
}
